import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useEffect, useMemo, useState } from 'react';
import FuseLoading from '@fuse/core/FuseLoading';
import InvoicePrintReport from 'app/main/PrintReport/InvoicePrintReport';
import CircularLoader from 'app/main/loadz/components/CircularLoader';
import client from 'client';
import { useTranslation } from 'react-i18next';
import {
  DATE_PERIODS,
  REPORT_TYPES,
  filterReportData,
  getDatePeriodRange,
} from 'app/main/PrintReport/salesReportUtils';

const YELLOW = '#edca33';
const RADIUS = '8px';

const labelSx = {
  fontSize: 13,
  fontWeight: 600,
  color: '#2c2e35',
  mb: 0.75,
};

const controlSx = {
  backgroundColor: '#fff',
  borderRadius: RADIUS,
  minHeight: 42,
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: '#d0d4dc',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: '#b8bdc8',
  },
};

const Field = ({ label, children }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography sx={labelSx}>{label}</Typography>
    {children}
  </Box>
);

export const InvoiceReportModal = ({
  open,
  onClose,
  setMonthSelector,
  reportDateStart,
  reportDateEnd,
  setReportDateStart,
  setReportDateEnd,
  reportFullLoading,
  generateInvoiceReport,
  selectedCustomers,
  setSelectedCustomers,
  user,
  setSelectedJobs,
  selectedJobs,
  selectedStatus,
  setSelectedStatus,
  fullReportData,
  compLogo,
  setGroupByCustomer,
  setGroupByJob,
  groupByJob,
  groupByCustomer,
  reportType,
  setReportType,
  datePeriod,
  setDatePeriod,
}) => {
  const [customersList, setCustomersList] = useState([]);
  const [jobsList, setJobsList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [invoiceSearch, setInvoiceSearch] = useState('');
  const { t } = useTranslation('navigation');

  const allCustomer = useMemo(() => ({ name: t('ALL_CUSTOMERS'), customer_id: 'all' }), [t]);
  const allJob = useMemo(() => ({ job_no: t('ALL_JOBS'), job_id: 'all' }), [t]);
  const statusOptions = useMemo(
    () => [
      { status: t('ALL'), id: 'all' },
      { status: t('OPEN_INVOICE'), id: 'pending' },
      { status: t('PAID_INVOICE'), id: 'paid' },
    ],
    [t]
  );

  const showDateRange = datePeriod !== DATE_PERIODS.ALL_DATES;

  const handlePeriodChange = (period) => {
    setDatePeriod(period);
    if (period === DATE_PERIODS.CUSTOM) return;
    const range = getDatePeriodRange(period);
    if (!range) return;
    setReportDateStart(range.start);
    setReportDateEnd(range.end);
    if (setMonthSelector) {
      const isMonthPeriod = period === DATE_PERIODS.THIS_MONTH || period === DATE_PERIODS.LAST_MONTH;
      setMonthSelector(isMonthPeriod && range.start ? range.start.slice(0, 7) : '0');
    }
  };

  useEffect(() => {
    if (!open) {
      setInvoiceSearch('');
      return undefined;
    }

    let cancelled = false;
    const loadFilters = async () => {
      setLoading(true);
      try {
        if (!reportDateStart && !reportDateEnd) {
          handlePeriodChange(DATE_PERIODS.THIS_MONTH);
        }
        const [customersResult, jobsResult] = await Promise.all([
          client.post('/invoices/customers-all', { compacctnum: user?.company?.compacctnum }),
          client.post('/invoices/jobs-all', { compacctnum: user?.company?.compacctnum }),
        ]);
        if (cancelled) return;
        setCustomersList([allCustomer, ...customersResult.data]);
        setJobsList([allJob, ...jobsResult.data]);
        if (!selectedCustomers.length) setSelectedCustomers([allCustomer]);
        if (!selectedJobs.length) setSelectedJobs([allJob]);
        if (!selectedStatus.length) setSelectedStatus([statusOptions[0]]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadFilters();
    return () => {
      cancelled = true;
    };
  }, [open]);

  const filteredReportData = useMemo(
    () => filterReportData(fullReportData, invoiceSearch),
    [fullReportData, invoiceSearch]
  );

  const handleReportTypeChange = (type) => {
    setReportType(type);
    if (type === REPORT_TYPES.SUMMARY) {
      setGroupByCustomer(true);
    }
  };

  const disableIfAllSelected = (selected, allId, optionId) => {
    const hasAll = selected.some(
      (item) => item === allId || item?.customer_id === allId || item?.job_id === allId || item?.id === allId
    );
    if (hasAll) return optionId !== allId ? true : selected.length > 0;
    if (selected.length > 0 && optionId === allId) return true;
    return false;
  };

  const hasReport = Boolean(filteredReportData?.invoices?.length);
  const emptyReport = Boolean(
    filteredReportData && Array.isArray(filteredReportData.invoices) && !filteredReportData.invoices.length
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={hasReport ? 'lg' : 'md'}
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: RADIUS,
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle
        sx={{
          background: YELLOW,
          m: 0,
          py: 0,
          px: 2,
          minHeight: 52,
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontWeight: 700,
          fontSize: 18,
          color: '#1f1f1f',
        }}
      >
        {t('SALES_REPORT')}
        <IconButton
          onClick={onClose}
          size='small'
          sx={{ position: 'absolute', right: 12, color: '#1f1f1f' }}
          aria-label={t('CLOSE')}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          px: 3,
          pt: '20px !important',
          pb: 2,
          overflowY: 'auto',
          maxHeight: hasReport ? '78vh' : 'none',
        }}
      >
        {loading ? (
          <Box sx={{ py: 6 }}>
            <FuseLoading />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                gap: 2,
              }}
            >
              <Field label={t('REPORT_TYPE')}>
                <FormControl fullWidth size='small'>
                  <Select
                    value={reportType}
                    onChange={(e) => handleReportTypeChange(e.target.value)}
                    sx={controlSx}
                  >
                    <MenuItem value={REPORT_TYPES.SUMMARY}>{t('SALES_BY_CUSTOMER')}</MenuItem>
                    <MenuItem value={REPORT_TYPES.INVOICE}>{t('SALES_BY_CUSTOMER_INVOICE')}</MenuItem>
                    <MenuItem value={REPORT_TYPES.DETAIL}>{t('SALES_BY_CUSTOMER_DETAIL')}</MenuItem>
                  </Select>
                </FormControl>
              </Field>
              <Field label={t('REPORT_PERIOD')}>
                <FormControl fullWidth size='small'>
                  <Select
                    value={datePeriod}
                    onChange={(e) => handlePeriodChange(e.target.value)}
                    sx={controlSx}
                  >
                    <MenuItem value={DATE_PERIODS.THIS_MONTH}>{t('THIS_MONTH')}</MenuItem>
                    <MenuItem value={DATE_PERIODS.LAST_MONTH}>{t('LAST_MONTH')}</MenuItem>
                    <MenuItem value={DATE_PERIODS.THIS_QUARTER}>{t('THIS_QUARTER')}</MenuItem>
                    <MenuItem value={DATE_PERIODS.LAST_QUARTER}>{t('LAST_QUARTER')}</MenuItem>
                    <MenuItem value={DATE_PERIODS.THIS_YEAR}>{t('THIS_YEAR')}</MenuItem>
                    <MenuItem value={DATE_PERIODS.LAST_YEAR}>{t('LAST_YEAR')}</MenuItem>
                    <MenuItem value={DATE_PERIODS.ALL_DATES}>{t('ALL_DATES')}</MenuItem>
                    <MenuItem value={DATE_PERIODS.CUSTOM}>{t('CUSTOM_DATE_RANGE')}</MenuItem>
                  </Select>
                </FormControl>
              </Field>
              {showDateRange && (
                <>
                  <Field label={t('START_DATE')}>
                    <TextField
                      type='date'
                      size='small'
                      fullWidth
                      value={reportDateStart || ''}
                      onChange={(e) => {
                        setDatePeriod(DATE_PERIODS.CUSTOM);
                        setReportDateStart(e.target.value);
                      }}
                      InputProps={{ sx: controlSx }}
                    />
                  </Field>
                  <Field label={t('END_DATE')}>
                    <TextField
                      type='date'
                      size='small'
                      fullWidth
                      value={reportDateEnd || ''}
                      onChange={(e) => {
                        setDatePeriod(DATE_PERIODS.CUSTOM);
                        setReportDateEnd(e.target.value);
                      }}
                      InputProps={{ sx: controlSx }}
                    />
                  </Field>
                </>
              )}
            </Box>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' },
                gap: 2,
              }}
            >
              <Field label={t('SELECT_CUSTOMERS')}>
                <Autocomplete
                  multiple
                  limitTags={1}
                  options={customersList}
                  getOptionDisabled={(option) =>
                    disableIfAllSelected(selectedCustomers, 'all', option.customer_id)
                  }
                  getOptionLabel={(option) => option.name || ''}
                  isOptionEqualToValue={(option, value) => option.customer_id === value.customer_id}
                  filterSelectedOptions
                  value={selectedCustomers}
                  onChange={(_, newValue) => setSelectedCustomers(newValue)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size='small'
                      variant='outlined'
                      InputProps={{ ...params.InputProps, sx: controlSx }}
                    />
                  )}
                  sx={{
                    '& .MuiChip-root': { height: 22, fontSize: 12 },
                    '& .MuiAutocomplete-inputRoot': { py: '4px !important' },
                  }}
                />
              </Field>
              <Field label={t('SELECT_JOBS')}>
                <Autocomplete
                  multiple
                  limitTags={1}
                  options={jobsList}
                  getOptionDisabled={(option) => disableIfAllSelected(selectedJobs, 'all', option.job_id)}
                  getOptionLabel={(option) => option.job_no || ''}
                  isOptionEqualToValue={(option, value) => option.job_id === value.job_id}
                  filterSelectedOptions
                  value={selectedJobs}
                  onChange={(_, newValue) => setSelectedJobs(newValue)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size='small'
                      variant='outlined'
                      InputProps={{ ...params.InputProps, sx: controlSx }}
                    />
                  )}
                  sx={{
                    '& .MuiChip-root': { height: 22, fontSize: 12 },
                    '& .MuiAutocomplete-inputRoot': { py: '4px !important' },
                  }}
                />
              </Field>
              <Field label={t('SELECT_STATUS')}>
                <Autocomplete
                  multiple
                  limitTags={1}
                  options={statusOptions}
                  getOptionDisabled={(option) => disableIfAllSelected(selectedStatus, 'all', option.id)}
                  getOptionLabel={(option) => option.status || ''}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  filterSelectedOptions
                  value={selectedStatus}
                  onChange={(_, newValue) => setSelectedStatus(newValue)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size='small'
                      variant='outlined'
                      InputProps={{ ...params.InputProps, sx: controlSx }}
                    />
                  )}
                  sx={{
                    '& .MuiChip-root': { height: 22, fontSize: 12 },
                    '& .MuiAutocomplete-inputRoot': { py: '4px !important' },
                  }}
                />
              </Field>
            </Box>

            {hasReport && (
              <Field label={t('SEARCH_INVOICE_OR_CUSTOMER')}>
                <TextField
                  size='small'
                  fullWidth
                  value={invoiceSearch}
                  onChange={(e) => setInvoiceSearch(e.target.value)}
                  InputProps={{ sx: controlSx }}
                />
              </Field>
            )}
          </Box>
        )}

        {hasReport ? (
          <>
            <Divider sx={{ my: 2 }} />
            <InvoicePrintReport
              display
              fullReportData={filteredReportData}
              date_start={reportDateStart}
              date_end={reportDateEnd}
              selectedCustomers={selectedCustomers}
              selectedJobs={selectedJobs}
              selectedStatus={selectedStatus}
              compLogo={compLogo}
              groupByJob={groupByJob}
              groupByCustomer={groupByCustomer}
              reportType={reportType}
              company_id={user?.company?.company_id}
            />
          </>
        ) : emptyReport ? (
          <Typography variant='body1' align='center' sx={{ mt: 3, color: 'text.secondary' }}>
            {t('NO_DATA_FOUND')}
          </Typography>
        ) : null}
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 1.5,
          borderTop: '1px solid #e6e6e6',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Tooltip title={t('TOTAL_BY_CUSTOMER_HINT')} placement='top'>
            <FormControlLabel
              sx={{ mr: 0 }}
              control={
                <Checkbox
                  checked={groupByCustomer}
                  onChange={(e) => {
                    e.stopPropagation();
                    setGroupByCustomer(e.target.checked);
                  }}
                />
              }
              label={<Typography sx={{ fontSize: 14, fontWeight: 600 }}>{t('TOTAL_BY_CUSTOMER')}</Typography>}
            />
          </Tooltip>
          <FormControlLabel
            sx={{ mr: 0 }}
            control={
              <Checkbox
                checked={groupByJob}
                onChange={(e) => {
                  e.stopPropagation();
                  setGroupByJob(e.target.checked);
                }}
              />
            }
            label={<Typography sx={{ fontSize: 14, fontWeight: 600 }}>{t('GROUP_BY_JOB')}</Typography>}
          />
        </Box>
        {reportFullLoading ? (
          <CircularLoader />
        ) : (
          <Button
            variant='contained'
            onClick={(e) => {
              e.stopPropagation();
              generateInvoiceReport();
            }}
            sx={{
              backgroundColor: YELLOW,
              color: '#1f1f1f',
              fontWeight: 700,
              px: 4,
              height: 40,
              borderRadius: RADIUS,
              boxShadow: 'none',
              '&:hover': { backgroundColor: '#d4b42c', boxShadow: 'none' },
            }}
          >
            {t('GENERATE_REPORT')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
